// Voice capture for hold-to-talk (ui/dictation.ts): the microphone, taken down to what the
// hub's /api/voice wants (16 kHz mono PCM16) and streamed there as it records (VoiceStream),
// plus a live level for the meter. No dependencies: an AudioWorklet (a ScriptProcessor where
// there is none) hands over the microphone's samples, which are averaged down to 16 kHz as
// they come.

import { sameOriginWs } from '../net/connection';

export const RATE = 16_000;
/** A recording stops on its own after this long, as in the apps (the hub takes no more). */
export const MAX_SECONDS = 300;

/** Why the microphone couldn't be used, in words for the person holding the button. */
export class MicError extends Error {}

function micError(e: unknown): MicError {
  const name = e instanceof DOMException ? e.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError')
    return new MicError('Microphone access is blocked. Allow the microphone for this site in the browser’s settings, then try again.');
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return new MicError('No microphone found. Connect one and try again.');
  if (name === 'NotReadableError' || name === 'AbortError') return new MicError('The microphone is in use by another app, or couldn’t start.');
  return new MicError(`Couldn’t start the microphone${e instanceof Error && e.message ? `: ${e.message}` : '.'}`);
}

// Posts the microphone's first channel in ~2048-frame chunks (a message per 128-frame render
// quantum would be ~375 a second).
const WORKLET = `
class CmxTap extends AudioWorkletProcessor {
  constructor() { super(); this.buf = new Float32Array(2048); this.n = 0; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) {
      for (let i = 0; i < ch.length; i++) {
        this.buf[this.n++] = ch[i];
        if (this.n === this.buf.length) { this.port.postMessage(this.buf.slice()); this.n = 0; }
      }
    }
    return true;
  }
}
registerProcessor('cmx-tap', CmxTap);
`;

/**
 * The microphone, recording until stop() or MAX_SECONDS. Create it within the press (a user
 * gesture: Safari only lets audio start in one), then await ready.
 */
export class Recorder {
  /** Loudness of the latest chunk, 0..1 (perceptual: -60 dB → 0, 0 dB → 1). */
  level = 0;
  /** Called once when MAX_SECONDS is reached (recording has stopped by then). */
  onLimit: (() => void) | null = null;
  /** Called with each piece of the recording (16 kHz mono PCM16) as it's heard. */
  onAudio: ((pcm: Int16Array) => void) | null = null;
  readonly ready: Promise<void>;

  private readonly ctx: AudioContext;
  private stream: MediaStream | null = null;
  private node: AudioNode | null = null;
  private n = 0; // samples recorded
  // Averaging down to RATE: the running sum, its count, and where the next output sample ends
  // (in input samples since the start).
  private sum = 0;
  private count = 0;
  private seen = 0;
  private edge: number;
  private readonly step: number;
  private stopped = false;

  constructor() {
    if (!navigator.mediaDevices?.getUserMedia) throw new MicError('Voice input needs a secure (https) page and a browser with microphone access.');
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new AC();
    void this.ctx.resume().catch(() => {});
    this.step = this.ctx.sampleRate / RATE;
    this.edge = this.step;
    this.ready = this.open();
  }

  private async open() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (e) {
      this.stop();
      throw micError(e);
    }
    if (this.stopped) {
      this.stop(); // released while the browser was asking
      return;
    }
    const src = this.ctx.createMediaStreamSource(this.stream);
    const take = (chunk: Float32Array) => this.take(chunk);
    try {
      const url = URL.createObjectURL(new Blob([WORKLET], { type: 'text/javascript' }));
      try {
        await this.ctx.audioWorklet.addModule(url);
      } finally {
        URL.revokeObjectURL(url);
      }
      const node = new AudioWorkletNode(this.ctx, 'cmx-tap', { numberOfInputs: 1, numberOfOutputs: 0 });
      node.port.onmessage = (e: MessageEvent<Float32Array>) => take(e.data);
      this.node = node;
    } catch {
      // No AudioWorklet (older Safari, some embedded browsers).
      const node = this.ctx.createScriptProcessor(2048, 1, 1);
      node.onaudioprocess = (e) => take(e.inputBuffer.getChannelData(0));
      node.connect(this.ctx.destination); // a ScriptProcessor only runs while connected; it outputs silence
      this.node = node;
    }
    if (this.stopped) return this.stop();
    src.connect(this.node);
  }

  private take(chunk: Float32Array) {
    if (this.stopped) return;
    const out = new Int16Array(Math.ceil(chunk.length / this.step) + 1);
    let k = 0;
    let sq = 0;
    for (let i = 0; i < chunk.length; i++) {
      const x = chunk[i];
      sq += x * x;
      this.sum += x;
      this.count++;
      if (++this.seen >= this.edge) {
        const v = Math.max(-1, Math.min(1, this.sum / this.count));
        out[k++] = v < 0 ? v * 0x8000 : v * 0x7fff;
        this.sum = this.count = 0;
        this.edge += this.step;
        if (++this.n === RATE * MAX_SECONDS) {
          this.onAudio?.(out.subarray(0, k));
          this.stop();
          this.onLimit?.();
          return;
        }
      }
    }
    if (k) this.onAudio?.(out.subarray(0, k));
    const db = 10 * Math.log10(sq / chunk.length || 1e-10);
    this.level = Math.max(0, Math.min(1, (db + 60) / 60));
  }

  /** Seconds recorded so far. */
  get seconds(): number {
    return this.n / RATE;
  }

  /** Stop recording and let go of the microphone (the browser's recording light goes out). */
  stop() {
    this.stopped = true;
    this.level = 0;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.node?.disconnect();
    if (this.ctx.state !== 'closed') void this.ctx.close().catch(() => {});
  }
}

/**
 * One recording on its way to the hub (GET /api/voice, a WebSocket): the audio as it's heard,
 * the words so far back (onPartial) while it goes on, and the text at the end.
 */
export class VoiceStream {
  /** Called with the words so far, as the hub hears more. */
  onPartial: ((text: string) => void) | null = null;

  private readonly ws: WebSocket;
  /** Audio heard before the socket opened. */
  private queued: ArrayBuffer[] = [];
  private readonly result: Promise<string>;
  private settle!: (r: { text: string } | { error: Error }) => void;

  constructor() {
    this.ws = new WebSocket(sameOriginWs('/api/voice'));
    this.ws.binaryType = 'arraybuffer';
    this.result = new Promise<string>((resolve, reject) => {
      let done = false;
      this.settle = (r) => {
        if (done) return;
        done = true;
        if ('text' in r) resolve(r.text);
        else reject(r.error);
      };
    });
    this.result.catch(() => {}); // a stream nobody ends (cancelled) fails quietly
    this.ws.onopen = () => {
      for (const b of this.queued) this.ws.send(b);
      this.queued = [];
    };
    this.ws.onmessage = (e: MessageEvent) => {
      if (typeof e.data !== 'string') return;
      const m = JSON.parse(e.data) as { type: string; text?: string; message?: string };
      if (m.type === 'partial') this.onPartial?.(m.text ?? '');
      else if (m.type === 'final') this.settle({ text: (m.text ?? '').trim() });
      else if (m.type === 'error') this.settle({ error: new Error(m.message || 'cmx couldn’t transcribe that.') });
    };
    // The handshake's refusals (signed out, too many recordings) come back as a bare close.
    this.ws.onclose = () => this.settle({ error: new Error('Can’t reach cmx. Check your connection.') });
  }

  /** More of the recording (16 kHz mono PCM16; WAV's little-endian, as browsers' platforms are). */
  send(pcm: Int16Array) {
    const b = pcm.slice().buffer;
    if (this.ws.readyState === WebSocket.OPEN) this.ws.send(b);
    else if (this.ws.readyState === WebSocket.CONNECTING) this.queued.push(b);
  }

  /** The recording is over: the text of it. */
  end(): Promise<string> {
    const end = JSON.stringify({ type: 'end' });
    if (this.ws.readyState === WebSocket.OPEN) this.ws.send(end);
    else this.ws.addEventListener('open', () => this.ws.send(end), { once: true }); // after the queued audio
    const timer = setTimeout(() => this.settle({ error: new Error('cmx took too long.') }), FINAL_WAIT_MS);
    return this.result.finally(() => {
      clearTimeout(timer);
      this.ws.close();
    });
  }

  /** Drops the recording (the hub stops on it). */
  cancel() {
    this.settle({ error: new Error('cancelled') });
    this.ws.close();
  }
}

/** The hub has the final text within 30 s of the end, or says why not. */
const FINAL_WAIT_MS = 35_000;
